/**
 * Learning Catalog: what Arta learned, toggled between two views on one screen: Conversations (the learning history: every
 * case Arta learned from, with the per-cause track record; a case can be undone and redone) and Manual (Learning editor
 * patterns and in-chat teaches, learned as soon as they are saved; searchable, editable, undoable and learnable again).
 * Both are compact tables of one server page each (newest first); a row opens its details and actions in a popup. Only the
 * shown view loads; each keeps its filters and its loaded page while the other is shown. The dropdown choices are loaded once
 * for the page and shared by both views, and so is the cache of how each opened conversation started (the popups' first
 * query and context). Route: /learning/catalog.
 */
import React, { useCallback, useRef, useState } from "react";
import { Loader2, RefreshCw, WifiOff } from "lucide-react";

import AdminSideBar from "../../AdminSideBar";
import UserProfileMenu from "../../UserProfileMenu";
import { getStoredRole } from "../../../utils/auth";
import LearningAgentSideBar from "../LearningAgentSideBar";
import LearningEditor from "../editor/LearningEditor";
import { editCatalogPattern } from "../useLearning";
import CatalogDetail from "./CatalogDetail";
import CatalogFilters, { ViewToggle } from "./CatalogFilters";
import CatalogPager from "./CatalogPager";
import CatalogTable from "./CatalogTable";
import DetailModal from "./DetailModal";
import HistoryView from "./HistoryView";
import useCatalog from "./useCatalog";
import useCatalogOptions from "./useCatalogOptions";
import useHistory from "./useHistory";
import { SessionContextCache } from "./useSessionContext";
import { STATUS } from "./usePagedList";
import { CATALOG_TEXT, CATALOG_VIEW, DEFAULT_VIEW, PAGE_SIZE } from "./catalogText";

const EDIT_RESULT = Object.freeze({ LEARNED: "learned", REFUSED: "refused" });
const REFUSALS = Object.freeze(["already_decided"]);

/** The editor's pre-fill for one catalog item (fix included: the editor reads it from the session only). */
const editorSession = (item) => ({
  category: { key: item.category, label: item.category },
  symptoms: item.symptoms || [],
  steps: item.resolution_steps || [],
  rootCause: item.root_cause || "",
  fixKind: item.fix_kind || "",
  fixText: item.fix_text || "",
});

const CatalogPage = ({ onLogout }) => {
  const isAdmin = getStoredRole() === "admin";
  const [view, setView] = useState(DEFAULT_VIEW);
  const manualView = view === CATALOG_VIEW.MANUAL;
  const catalog = useCatalog({ enabled: manualView });
  const history = useHistory({ enabled: !manualView });
  const { status, items, total, offset, setOffset, filters, setFilter, clearFilters, refresh, error } = catalog;
  const { products, historyOptions, refreshProducts } = useCatalogOptions({ manualState: filters.state, historyShown: !manualView });
  const current = manualView ? catalog : history;
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState("");
  const saved = useRef(false); // the editor closes the same way after a save and after a cancel
  const [sessionContexts] = useState(() => new Map()); // how each opened conversation started, kept for the page's lifetime

  // A write changes the counts and may change the other view: the hidden one reloads when it is next shown.
  const changed = (text) => { setNotice(text); refresh(); refreshProducts(); history.refresh(); };
  const historyChanged = () => { catalog.refresh(); refreshProducts(); };
  const onRefresh = () => { current.refresh(); if (manualView) refreshProducts(); };
  const onFilter = (key, value) => { setNotice(""); setFilter(key, value); };
  const onView = (value) => { setNotice(""); setView(value); };
  const openDetail = useCallback((item) => { setNotice(""); setDetail(item); }, []);

  const handleEditSubmit = async (payload) => {
    try {
      await editCatalogPattern(editing.id, {
        symptoms: payload.symptoms,
        resolution_steps: payload.resolution_steps,
        root_cause: payload.root_cause,
        fix_kind: payload.fix_kind || "",
        fix_text: payload.fix_text || "",
      });
      saved.current = true;
      return { kind: EDIT_RESULT.LEARNED };
    } catch (e) {
      if (REFUSALS.includes(e && e.message)) return { kind: EDIT_RESULT.REFUSED, reason: e.message };
      throw e;
    }
  };
  const openEditor = (item) => { saved.current = false; setDetail(null); setEditing(item); };
  const detailChanged = (text) => { setDetail(null); changed(text); };
  const handleEditClose = () => {
    setEditing(null);
    if (saved.current) changed(CATALOG_TEXT.notices.edited);
  };

  return (
    <SessionContextCache.Provider value={sessionContexts}>
      <div className="flex h-screen w-full bg-[#FAFAF6] overflow-hidden">
        {isAdmin ? <AdminSideBar onLogout={onLogout} /> : <LearningAgentSideBar onLogout={onLogout} />}
        <div className="flex-1 relative flex flex-col px-6 sm:px-10 lg:px-16 xl:px-24 py-8 overflow-y-auto overflow-x-hidden min-w-0">
          <div className="absolute top-4 right-6 z-50"><UserProfileMenu onLogout={onLogout} /></div>
          <div className="w-full min-w-0">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-[#253439]">{CATALOG_TEXT.title}</h1>
                <p className="text-gray-500 text-sm mt-1">{CATALOG_TEXT.description[view]}</p>
              </div>
              <button type="button" onClick={onRefresh} disabled={current.status === STATUS.LOADING}
                className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-50">
                <RefreshCw size={14} className={current.status === STATUS.LOADING ? "animate-spin" : ""} />{CATALOG_TEXT.refresh}
              </button>
            </div>

            <div className="mb-4"><ViewToggle value={view} onChange={onView} /></div>
            {manualView ? (
              <>
                <CatalogFilters filters={filters} products={products} onChange={onFilter} onClear={() => { setNotice(""); clearFilters(); }} />

                {notice && <p className="mb-3 text-sm text-emerald-700" role="status">{notice}</p>}
                {status === STATUS.LOADING && items.length === 0 && (
                  <p className="flex items-center gap-2 text-sm text-gray-500" role="status"><Loader2 size={14} className="animate-spin" />{CATALOG_TEXT.loading}</p>
                )}
                {status === STATUS.NOT_CONNECTED && (
                  <p className="flex items-center gap-2 text-sm text-gray-500" role="status"><WifiOff size={14} />{CATALOG_TEXT.notConnected}</p>
                )}
                {status === STATUS.FAILED && <p className="text-sm text-red-600" role="alert">{error || CATALOG_TEXT.failed}</p>}
                {status === STATUS.READY && items.length === 0 && <p className="text-sm text-gray-500">{CATALOG_TEXT.empty}</p>}
                {items.length > 0 && status !== STATUS.FAILED && (
                  <>
                    <CatalogTable items={items} loading={status === STATUS.LOADING} onOpen={openDetail} />
                    <CatalogPager offset={offset} limit={PAGE_SIZE} total={total} onOffset={setOffset} />
                  </>
                )}
              </>
            ) : <HistoryView history={history} options={historyOptions} products={products} onChanged={historyChanged} />}
          </div>
        </div>
        {detail && (
          <DetailModal title={CATALOG_TEXT.detail.title} onClose={() => setDetail(null)}>
            <CatalogDetail item={detail} onEdit={openEditor} onChanged={detailChanged} />
          </DetailModal>
        )}
        {editing && (
          <LearningEditor session={editorSession(editing)} onSubmit={handleEditSubmit} onClose={handleEditClose}
            heading={CATALOG_TEXT.actions.editHeading} saveLabel={CATALOG_TEXT.actions.saveEdit} />
        )}
      </div>
    </SessionContextCache.Provider>
  );
};

export default CatalogPage;
